import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import {
  acceptAllCookies,
  COOKIE_PREFERENCES_OPEN_EVENT,
  CookieConsentPreferences,
  getCookieConsent,
  hasCookieConsentDecision,
  rejectNonEssentialCookies,
  saveCookieConsent,
} from '../cookieConsent';
import { applyAnalyticsConsent } from '../analytics';

const CookiePreferencesModal: React.FC<{
  isOpen: boolean;
  initialPreferences: CookieConsentPreferences;
  onClose: () => void;
  onSave: (preferences: CookieConsentPreferences) => void;
}> = ({ isOpen, initialPreferences, onClose, onSave }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(initialPreferences.analytics);

  useEffect(() => {
    if (isOpen) {
      setAnalyticsEnabled(initialPreferences.analytics);
    }
  }, [isOpen, initialPreferences.analytics]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-preferences-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close cookie preferences"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="cookie-preferences-title" className="text-lg font-semibold text-gray-900">
              Cookie preferences
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Choose which optional cookies we may use. Essential cookies are always active.
            </p>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-medium text-gray-900">Strictly necessary</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Required for sign-in, security, session management, and core booking features. These cannot be
                  disabled.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                Always on
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-medium text-gray-900">Analytics</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Helps us understand how visitors use the site (Google Analytics). Data is aggregated and used to
                  improve the platform.
                </p>
              </div>
              <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={analyticsEnabled}
                  onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                />
                <span className="h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-[#0B4778] transition-colors" />
                <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button type="button" className="aacore-btn-outlined w-full justify-center px-4 py-2 text-sm sm:w-auto" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="aacore-btn-primary w-full justify-center px-4 py-2 text-sm sm:w-auto"
            onClick={() =>
              onSave({
                necessary: true,
                analytics: analyticsEnabled,
              })
            }
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export const CookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(() => !hasCookieConsentDecision());
  const [showPreferences, setShowPreferences] = useState(false);
  const [draftPreferences, setDraftPreferences] = useState<CookieConsentPreferences>(() => {
    const stored = getCookieConsent();
    return stored?.preferences ?? { necessary: true, analytics: false };
  });

  const applyPreferences = useCallback((preferences: CookieConsentPreferences) => {
    saveCookieConsent(preferences);
    applyAnalyticsConsent(preferences.analytics);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const record = acceptAllCookies();
    applyAnalyticsConsent(record.preferences.analytics);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const handleRejectNonEssential = useCallback(() => {
    const record = rejectNonEssentialCookies();
    applyAnalyticsConsent(record.preferences.analytics);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const openPreferences = useCallback(() => {
    const stored = getCookieConsent();
    setDraftPreferences(stored?.preferences ?? { necessary: true, analytics: false });
    setShowPreferences(true);
  }, []);

  useEffect(() => {
    const handleOpenPreferences = () => {
      openPreferences();
    };

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
    return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
  }, [openPreferences]);

  return (
    <>
      {showBanner && (
        <div
          className="fixed bottom-0 inset-x-0 z-[9999] border-t border-[#B9D2EE] shadow-[0_-8px_24px_rgba(15,23,42,0.12)]"
          style={{ backgroundColor: '#E8F1FB' }}
          role="region"
          aria-label="Cookie consent"
        >
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:gap-6 sm:py-5">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-[#0B4778]" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">We use cookies</p>
                <p className="mt-1 text-sm text-gray-700">
                  We use essential cookies to run AACore Booking and optional analytics cookies to understand site
                  usage. You can accept all, reject non-essential cookies, or customize your choices.{' '}
                  <Link to="/privacy#cookies-tracking" className="text-[#0B4778] underline hover:text-[#0b3f73]">
                    Learn more
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <button
                type="button"
                className="aacore-btn-outlined w-full justify-center px-4 py-2 text-sm sm:w-auto"
                onClick={handleRejectNonEssential}
              >
                Reject non-essential
              </button>
              <button
                type="button"
                className="aacore-btn-outlined w-full justify-center px-4 py-2 text-sm sm:w-auto"
                onClick={openPreferences}
              >
                Customize
              </button>
              <button
                type="button"
                className="aacore-btn-primary w-full justify-center px-4 py-2 text-sm sm:w-auto"
                onClick={handleAcceptAll}
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      <CookiePreferencesModal
        isOpen={showPreferences}
        initialPreferences={draftPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={applyPreferences}
      />
    </>
  );
};

export const CookieSettingsLink: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className = '',
  style,
}) => (
  <button
    type="button"
    className={className || 'text-blue-900 hover:text-blue-800 hover:underline'}
    style={style}
    onClick={() => window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT))}
  >
    Cookie settings
  </button>
);
